import useApi from "@/hooks/useApi";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Trigger, WorkflowListItem, triggerDefs } from "./types";
import IllustrationMyWorkflows from "@/icons/illustrations/IllustrationMyWorkflows";
import { Checkbox, WorkflowItem } from "./WorkflowHelpers";
import useWorkflows from "@/hooks/useWorkflows";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import useActivityLog from "@/hooks/useActivityLog";
import { useNavigate } from "react-router-dom";
import BulkActionBar from "@/components/BulkActionBar";

const CATEGORIES = ["All", "Lead Capture", "Support", "Sales", "Onboarding", "Custom", "Legacy"];

type StatusFilter = "active" | "all" | "archived";
type SortField = "name" | "createdBy" | "dateCreated" | "lastModified";
type SortDir = "asc" | "desc";

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "all", label: "All" },
  { value: "archived", label: "Archived" },
];

function sessionGet<T>(key: string, fallback: T): T {
  try {
    const v = sessionStorage.getItem(key);
    return v !== null ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}

function sessionSet(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch { /* quota exceeded or private browsing — ignore */ }
}

export default function Workflows() {
  const { workflows, setWorkflows, fetch } = useWorkflows();
  const api = useApi();
  const { track } = useActivityLog();
  const { displayToast } = useToastAlert();
  const navigate = useNavigate();
  const [categoryFilter, setCategoryFilter] = useState<string>("All");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() =>
    sessionGet("wf.status", "active")
  );
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [sortField, setSortField] = useState<SortField>("lastModified");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [recentlyHiddenIds, setRecentlyHiddenIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [categoryFilter, statusFilter]);

  const templates = useMemo(
    () => (Array.isArray(workflows) ? workflows.filter((w) => w.isTemplate) : []),
    [workflows]
  );
  const regularWorkflows = useMemo(
    () => (Array.isArray(workflows) ? workflows.filter((w) => !w.isTemplate) : []),
    [workflows]
  );

  const visibleWorkflows = useMemo(() => {
    const filtered = regularWorkflows.filter((w) => {
      const isArchived = w.workflowStatus === "deprecated";
      if (statusFilter === "active" && (isArchived || !w.enabled)) return false;
      if (statusFilter === "archived" && !isArchived) return false;
      if (categoryFilter !== "All" && (w.category ?? "") !== categoryFilter) return false;
      return true;
    });

    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "name":
          cmp = (a.name ?? "").localeCompare(b.name ?? "");
          break;
        case "createdBy":
          cmp = (a.createdBy ?? "").localeCompare(b.createdBy ?? "");
          break;
        case "dateCreated":
          cmp = (a.dateCreated ?? "") < (b.dateCreated ?? "") ? -1 : 1;
          break;
        case "lastModified":
          cmp = (a.lastModified ?? "") < (b.lastModified ?? "") ? -1 : 1;
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [regularWorkflows, categoryFilter, statusFilter, sortField, sortDir]);

  const groupedWorkflows = useMemo(() => {
    const result: Record<string, WorkflowListItem[]> = {};
    visibleWorkflows.forEach((workflow) => {
      if (!result[workflow.triggerType]) {
        result[workflow.triggerType] = [];
      }
      result[workflow.triggerType].push(workflow);
    });
    return result;
  }, [visibleWorkflows]);

  const triggers = useMemo(
    () => Object.keys(groupedWorkflows) as Trigger[],
    [groupedWorkflows]
  );

  if (workflows.length === 0) {
    return (
      <div className="p-16 bg-white rounded-2xl border border-slate-200 flex-col justify-center items-center gap-4 inline-flex">
        <div className="flex-col justify-center items-center gap-6 flex">
          <IllustrationMyWorkflows />
          <div className="flex-col justify-center items-center gap-2 flex">
            <div className="text-slate-800 text-lg">Workflows</div>
            <div className="text-center text-slate-500 text-sm">
              Your workflow list is currently empty.
              <br />
              Build your first workflow to enhance customer experiences and
              automate your processes.
            </div>
          </div>
        </div>
      </div>
    );
  }

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return <span className="text-slate-300 ml-1">↕</span>;
    return <span className="text-primary ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>;
  }

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll(ids: number[]) {
    const allSelected = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }

  async function toggleWorkflow(id: number) {
    if (!workflows) return;

    const index = workflows.findIndex((w) => w.id === id);
    if (index === -1) return;
    const workflow = workflows[index];
    const wasEnabled = workflow.enabled;

    // Optimistic update
    const clone = [...workflows];
    clone[index] = { ...workflow, enabled: !wasEnabled };
    setWorkflows(clone);

    if (wasEnabled) {
      setRecentlyHiddenIds(prev => new Set([...prev, id]));
    } else {
      setRecentlyHiddenIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }

    try {
      await api.messaging.post(`Workflows/toggle/${id}`);
    } catch {
      // Revert on failure
      const reverted = [...clone];
      reverted[index] = { ...workflow, enabled: wasEnabled };
      setWorkflows(reverted);
      displayToast(Severity.Error, "Failed to update workflow status");
      return;
    }

    track({
      category: "Workflows",
      action: wasEnabled ? "WorkflowDisabled" : "WorkflowEnabled",
      entityType: "Workflow",
      entityId: String(id),
      entityName: workflow.name
    });
  }

  async function removeWorkflow(id: number) {
    if (workflows === null) return;

    const workflow = workflows.find((w) => w.id === id);
    try {
      const resp = await api.messaging.delete(`Workflows/${id}`);
      if (resp.status === 409) {
        const body = await resp.json().catch(() => ({}));
        displayToast(Severity.Error, body?.error ?? "Cannot delete this template. Detach linked workflows first.");
        return;
      }
    } catch {
      displayToast(Severity.Error, "Failed to delete workflow");
      return;
    }

    const clone = workflows?.filter((w) => w.id !== id);
    setWorkflows(clone);

    track({
      category: "Workflows",
      action: "WorkflowDeleted",
      entityType: "Workflow",
      entityId: String(id),
      entityName: workflow?.name
    });
  }

  async function detachWorkflow(id: number) {
    try {
      await api.messaging.post(`Workflows/detach/${id}`);
      const clone = workflows.map((w) =>
        w.id === id ? { ...w, templateId: null, templateName: null } : w
      );
      setWorkflows(clone);
      displayToast(Severity.Success, "Workflow detached. You can now edit it independently.");

      track({
        category: "Workflows",
        action: "WorkflowDetachedFromTemplate",
        entityType: "Workflow",
        entityId: String(id)
      });
    } catch {
      displayToast(Severity.Error, "Failed to detach workflow from template");
    }
  }

  // Single server-side duplicate — see docs/api-patterns.md for why we never
  // do multi-step client-side create+fill patterns (was: GET->POST->PUT, replaced 2026-03-15).
  async function duplicateWorkflow(id: number) {
    try {
      const resp = await api.messaging.post(`Workflows/${id}/duplicate`, {});
      if (!resp.ok) {
        displayToast(Severity.Error, "Failed to duplicate workflow");
        return;
      }
      const body = await resp.json();
      await fetch();
      displayToast(Severity.Success, `Workflow duplicated: "${body?.name ?? "copy"}" (disabled — enable when ready)`);
    } catch (e) {
      console.error("Failed to duplicate workflow", e);
      displayToast(Severity.Error, "Failed to duplicate workflow");
    }
  }

  async function archiveWorkflow(id: number, replacedById?: number) {
    try {
      const resp = await api.messaging.patch(`Workflows/${id}/status`, { status: "deprecated", replacedById: replacedById ?? null });
      if (!resp.ok) {
        displayToast(Severity.Error, "Failed to archive workflow");
        return;
      }
      setWorkflows(
        workflows.map((w) =>
          w.id === id ? { ...w, workflowStatus: "deprecated", replacedById: replacedById ?? null, enabled: false } : w
        )
      );
      if (statusFilter === "active") {
        setRecentlyHiddenIds(prev => new Set([...prev, id]));
      }
      displayToast(Severity.Success, "Workflow archived");
    } catch {
      displayToast(Severity.Error, "Failed to archive workflow");
    }
  }

  async function unarchiveWorkflow(id: number) {
    try {
      const resp = await api.messaging.patch(`Workflows/${id}/status`, { status: "active", replacedById: null });
      if (!resp.ok) {
        displayToast(Severity.Error, "Failed to unarchive workflow");
        return;
      }
      // Unarchiving restores to DISABLED state — user must consciously re-enable
      setWorkflows(
        workflows.map((w) =>
          w.id === id ? { ...w, workflowStatus: null, replacedById: null, enabled: false } : w
        )
      );
      displayToast(Severity.Success, "Workflow unarchived (disabled — enable when ready)");
    } catch {
      displayToast(Severity.Error, "Failed to unarchive workflow");
    }
  }

  async function renameWorkflow(id: number, newName: string) {
    try {
      const resp = await api.messaging.patch(`Workflows/${id}/name`, { name: newName });
      if (!resp.ok) {
        displayToast(Severity.Error, "Failed to rename workflow");
        return;
      }
      setWorkflows(workflows.map(w => w.id === id ? { ...w, name: newName } : w));
    } catch {
      displayToast(Severity.Error, "Failed to rename workflow");
    }
  }

  async function bulkArchive() {
    const ids = [...selectedIds];
    await Promise.all(
      ids.map((id) => api.messaging.patch(`Workflows/${id}/status`, { status: "deprecated", replacedById: null }))
    );
    setWorkflows(
      workflows.map((w) => ids.includes(w.id) ? { ...w, workflowStatus: "deprecated", enabled: false } : w)
    );
    setSelectedIds(new Set());
    displayToast(Severity.Success, `${ids.length} workflow${ids.length > 1 ? "s" : ""} archived.`);
  }

  async function bulkEnable() {
    const ids = [...selectedIds];
    for (const id of ids) {
      await api.messaging.post(`Workflows/toggle/${id}`);
    }
    setWorkflows(workflows.map((w) => ids.includes(w.id) ? { ...w, enabled: true } : w));
    setSelectedIds(new Set());
    displayToast(Severity.Success, `${ids.length} workflow${ids.length > 1 ? "s" : ""} enabled.`);
  }

  async function bulkDisable() {
    const ids = [...selectedIds];
    for (const id of ids) {
      await api.messaging.post(`Workflows/toggle/${id}`);
    }
    setWorkflows(workflows.map((w) => ids.includes(w.id) ? { ...w, enabled: false } : w));
    setSelectedIds(new Set());
    displayToast(Severity.Success, `${ids.length} workflow${ids.length > 1 ? "s" : ""} disabled.`);
  }

  function renderRecentlyHidden() {
    if (statusFilter !== "active") return null;
    const disabledItems = regularWorkflows.filter(
      w => recentlyHiddenIds.has(w.id) && !w.enabled && w.workflowStatus !== "deprecated"
    );
    const archivedItems = regularWorkflows.filter(
      w => recentlyHiddenIds.has(w.id) && w.workflowStatus === "deprecated"
    );
    if (disabledItems.length === 0 && archivedItems.length === 0) return null;
    return (
      <div className="mx-4 mt-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-slate-600">Hidden from this view</span>
          <button
            type="button"
            onClick={() => setRecentlyHiddenIds(new Set())}
            className="ml-auto text-xs text-slate-400 hover:text-slate-600"
          >
            Clear
          </button>
        </div>
        {disabledItems.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-1">
            <span className="text-xs text-slate-400 self-center">Disabled:</span>
            {disabledItems.map(w => (
              <button
                key={w.id}
                type="button"
                onClick={() => toggleWorkflow(w.id)}
                className="flex items-center gap-1.5 text-xs bg-white border border-slate-300 rounded-full px-3 py-1 hover:bg-slate-100 text-slate-700 transition-colors"
                title="Click to re-enable"
              >
                <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {w.name}
              </button>
            ))}
          </div>
        )}
        {archivedItems.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-400 self-center">Archived:</span>
            {archivedItems.map(w => (
              <span
                key={w.id}
                className="text-xs bg-white border border-slate-300 rounded-full px-3 py-1 text-slate-500"
                title="Switch to Archived view to restore"
              >
                {w.name}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderBulkBar() {
    if (selectedIds.size === 0) return null;
    return (
      <div className="mx-4 mb-2">
        <BulkActionBar>
          <span className="font-medium">{selectedIds.size} selected</span>
          <div className="flex-1" />
          <button type="button" onClick={bulkEnable} className="px-3 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium">
            Enable
          </button>
          <button type="button" onClick={bulkDisable} className="px-3 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium">
            Disable
          </button>
          <button type="button" onClick={bulkArchive} className="px-3 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium">
            Archive
          </button>
          <button type="button" onClick={() => setSelectedIds(new Set())} className="px-2 py-1 rounded border border-transparent hover:bg-slate-100 text-slate-500 text-xs">
            Clear
          </button>
        </BulkActionBar>
      </div>
    );
  }

  function renderFilterBar() {
    return (
      <div className="px-4 pt-2 pb-1 flex items-center gap-4 flex-wrap">
        <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => {
                setStatusFilter(tab.value);
                sessionSet("wf.status", tab.value);
              }}
              className={`text-xs px-3 py-1 rounded-md transition-colors font-medium ${
                statusFilter === tab.value
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 font-medium">Category</label>
          <select
            className="text-sm border border-slate-200 rounded px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-hover"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  function renderTemplatesSection() {
    if (templates.length === 0) return null;

    return (
      <div className="p-4">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex gap-2 items-center font-bold text-violet-700">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
            Workflow Templates
          </div>
          <span className="text-xs text-slate-400">
            Changes to a template propagate to all linked workflows automatically
          </span>
        </div>
        <div className="bg-violet-50 border rounded-lg border-violet-200">
          <table cellPadding={10} className="w-full text-left">
            <thead>
              <tr className="border-b border-violet-200 h-12 text-xs text-slate-500 font-semibold">
                <th className="w-1/12">
                  <div className="flex items-center pl-2">
                    <Checkbox />
                  </div>
                </th>
                <th className="w-1/4">Name</th>
                <th className="w-1/4">Created by</th>
                <th className="w-1/8">Created</th>
                <th className="w-1/8">Updated</th>
                <th className="w-1/6">Status</th>
                <th className="w-1/12 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template, i) => {
                const linked = regularWorkflows.filter((w) => w.templateId === template.id).length;
                return (
                  <WorkflowItem
                    key={template.id}
                    item={template}
                    isLast={i === templates.length - 1}
                    toggle={() => toggleWorkflow(template.id)}
                    remove={() => removeWorkflow(template.id)}
                    duplicate={() => duplicateWorkflow(template.id)}
                    onRename={renameWorkflow}
                    linkedCount={linked}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderTrigger(trigger: Trigger, index: number) {
    const triggerWorkflows = groupedWorkflows[trigger];
    const triggerIds = triggerWorkflows.map((w) => w.id);
    const allSelected = triggerIds.length > 0 && triggerIds.every((id) => selectedIds.has(id));

    return (
      <div className="p-4" key={index}>
        <div className="mb-4 flex">
          <div className="flex gap-2 items-center font-bold">
            {triggerDefs[trigger]}
          </div>
        </div>
        <div className="bg-white border rounded-lg border-slate-200">
          <table cellPadding={10} className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 h-12 text-xs text-slate-500 font-semibold">
                <th className="w-1/12">
                  <div className="flex items-center pl-2">
                    <Checkbox
                      checked={allSelected}
                      onChange={() => selectAll(triggerIds)}
                    />
                  </div>
                </th>
                <th
                  className="w-1/4 cursor-pointer select-none hover:text-slate-700"
                  onClick={() => toggleSort("name")}
                >
                  Name <SortIcon field="name" />
                </th>
                <th
                  className="w-1/4 cursor-pointer select-none hover:text-slate-700"
                  onClick={() => toggleSort("createdBy")}
                >
                  Created by <SortIcon field="createdBy" />
                </th>
                <th
                  className="w-1/8 cursor-pointer select-none hover:text-slate-700"
                  onClick={() => toggleSort("dateCreated")}
                >
                  Created <SortIcon field="dateCreated" />
                </th>
                <th
                  className="w-1/8 cursor-pointer select-none hover:text-slate-700"
                  onClick={() => toggleSort("lastModified")}
                >
                  Updated <SortIcon field="lastModified" />
                </th>
                <th className="w-1/6">Status</th>
                <th className="w-1/12 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {triggerWorkflows.map((workflow, i) => (
                <WorkflowItem
                  key={workflow.id}
                  item={workflow}
                  isLast={i === triggerWorkflows.length - 1}
                  isSelected={selectedIds.has(workflow.id)}
                  onSelect={() => toggleSelect(workflow.id)}
                  toggle={() => toggleWorkflow(workflow.id)}
                  remove={() => removeWorkflow(workflow.id)}
                  duplicate={() => duplicateWorkflow(workflow.id)}
                  onDetach={workflow.templateId ? () => detachWorkflow(workflow.id) : undefined}
                  onArchive={workflow.workflowStatus !== "deprecated" ? (replacedById) => archiveWorkflow(workflow.id, replacedById) : undefined}
                  onUnarchive={workflow.workflowStatus === "deprecated" ? () => unarchiveWorkflow(workflow.id) : undefined}
                  onRename={renameWorkflow}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <>
      {renderTemplatesSection()}
      {renderFilterBar()}
      {renderRecentlyHidden()}
      {renderBulkBar()}
      {triggers.map(renderTrigger)}
    </>
  );
}
